'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { SchoolChoice } from '@/lib/grants-embed/input';

const MIN_QUERY = 3;
const DEBOUNCE_MS = 250;

export function choiceLabel(c: SchoolChoice): { title: string; subtitle: string } {
  const place = [c.city, c.state].filter(Boolean).join(', ');
  if (c.schoolName) return { title: c.schoolName, subtitle: [c.districtName, place].filter(Boolean).join(', ') };
  return { title: c.districtName, subtitle: place ? `School district in ${place}` : 'School district' };
}

/**
 * Search-as-you-type over schools and districts (an ARIA combobox). The query
 * the visitor typed is reported alongside the pick — it's stored on the lead.
 * Pass `inputId` when an outside <label> names the field.
 */
export function SchoolTypeahead({
  onPick,
  inputId,
  invalid = false,
  placeholder = 'Start typing your school or district',
  autoFocus = false,
}: {
  onPick: (choice: SchoolChoice, query: string) => void;
  inputId?: string;
  invalid?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const listId = useId();
  const fieldId = inputId ?? `${listId}-input`;
  const [query, setQuery] = useState('');
  const [choices, setChoices] = useState<SchoolChoice[]>([]);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    abortRef.current?.abort();
    if (q.length < MIN_QUERY) {
      setChoices([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/embed/grants/schools?q=${encodeURIComponent(q)}`, { signal: controller.signal });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error ?? 'Search is unavailable right now.');
        setChoices(data.choices);
        setActive(data.choices.length ? 0 : -1);
        setError(null);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setError((err as Error).message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  function pick(choice: SchoolChoice) {
    onPick(choice, query.trim());
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!choices.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % choices.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + choices.length) % choices.length);
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      pick(choices[active]);
    }
  }

  const showEmpty = !loading && !error && query.trim().length >= MIN_QUERY && choices.length === 0;

  return (
    <div className="relative">
      {!inputId && (
        <label htmlFor={fieldId} className="sr-only">
          School or district name
        </label>
      )}
      <div
        className={`flex items-center gap-2 rounded-[var(--fmxe-border-radius-sm)] border bg-white px-2 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--g-focus)] ${
          invalid ? 'border-[var(--fmxe-color-red)]' : 'border-[var(--g-field)]'
        }`}
      >
        <input
          id={fieldId}
          role="combobox"
          aria-invalid={invalid || undefined}
          aria-expanded={choices.length > 0}
          aria-controls={listId}
          aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          data-focus-ring="parent"
          className="h-11 w-full bg-transparent text-[length:var(--fmxe-font-family-size-sm)] text-[var(--fmxe-color-gray-core)] outline-none placeholder:text-[var(--fmxe-color-gray-light-2)]"
        />
        {loading && <Loader2 className="size-4 shrink-0 animate-spin text-[var(--g-slate)]" aria-label="Searching" />}
      </div>

      {choices.length > 0 && (
        <ul id={listId} role="listbox" className="absolute inset-x-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-[var(--fmxe-border-radius-sm)] border border-[var(--g-line)] bg-white shadow-lg">
          {choices.map((c, i) => {
            const { title, subtitle } = choiceLabel(c);
            return (
              <li
                key={`${c.ncesDistrictId}:${c.ncesSchoolId ?? ''}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(c);
                }}
                className={`cursor-pointer border-b border-[var(--g-line)] px-3 py-2.5 text-left last:border-b-0 ${
                  i === active ? 'bg-[var(--g-accent-wash)]' : ''
                }`}
              >
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs text-[var(--g-slate)]">{subtitle}</span>
              </li>
            );
          })}
        </ul>
      )}
      {showEmpty && (
        <p className="mt-2 text-sm text-[var(--g-slate)]">No schools or districts match. Try the full name, like &ldquo;Lincoln Elementary&rdquo;.</p>
      )}
      {error && (
        <p className="mt-2 text-sm text-[var(--fmxe-color-red)]" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
